import { AppRoutesPaths, IRouteDefinition, RouteAccess } from './types';
import {
  ConfirmSignUp,
  CreatePassword,
  NotFound,
  OAuthToken,
  ResetPassword,
  SignIn,
  SignUp,
  setPendingAuthEvent,
  trackEvent,
  utmParams,
  AnalyticsEvent,
  AuthEventType,
  LoginMethod,
} from '@sandtech-hub-monorepo/ui-auth-kit';
import { LayoutType } from '@sandtech-hub-monorepo/ui-kit';
import { ReactComponent as FitLogo } from '../assets/Icons/logo.svg';
import { ReactComponent as GoogleLogo } from '../assets/Icons/google-logo.svg';
import { ReactComponent as LinkedInLogo } from '../assets/Icons/linkedin-icon.svg';
import forgotPasswordCover from '../assets/forgot-password-cover.webp';
import loginCover from '../assets/login-cover.webp';
import signUpCover from '../assets/sign-up-cover.webp';
import changePasswordCover from '../assets/change-password-cover.webp';
import Home from '../pages/Home';
import { DegreeHome } from '../pages/Home/DegreeHome';
import { ProgramPage } from '../pages/Program';
import { ProgramsPage } from '../pages/Programs';
import { SettingsPage } from '../pages/Settings';
import { TranscriptPage } from '../pages/Transcript';
import { TranscriptVerify } from '../pages/TranscriptVerify';
import { CertificateVerify } from '../pages/CertificateVerify';
import { BadgeVerify } from '../pages/BadgeVerify';
import { Onboarding } from '../pages/Onboarding';
import { Profile } from '../pages/Profile';
import { Plans } from '../pages/Plans';
import { SubscribeCheckout } from '../pages/SubscribeCheckout';
import { SubscribeConfirmation } from '../pages/SubscribeConfirmation';
import { LearningPage } from '../pages/Learning';
import { CommunityPage } from '../pages/Community';
import { FIT_HELP_CENTER_URL, Logo } from '@sandtech-hub-monorepo/ui-kit-site';

const fitCoverLogo = <FitLogo className="h-28 w-auto text-white" />;
const fitCoverOverlayClassName =
  'bg-gradient-to-b from-[50.963%] from-heading/[0.34] to-[84.489%] to-heading/[0.91]';
const fitSocialButtonsClassName =
  'bg-background text-heading border hover:bg-card sm:hover:bg-background-tertiary';
const signInSocialButtonsClassName =
  'h-12 w-full rounded-[18px] border bg-background text-heading hover:bg-card sm:hover:bg-background-tertiary';
const signInInputClassName =
  '!h-11 !rounded-lg shadow-[0_1px_2px_rgba(16,24,40,0.05)]';

export const AppRoutes: Record<
  RouteAccess,
  Record<string, IRouteDefinition>
> = {
  private: {
    home: {
      name: 'home',
      element: <Home />,
      path: AppRoutesPaths.Home,
      layout: LayoutType.Fullscreen,
    },
    program: {
      name: 'program',
      element: <ProgramPage />,
      path: AppRoutesPaths.Program,
      layout: LayoutType.Fullscreen,
    },
    programs: {
      name: 'programs',
      element: <ProgramsPage />,
      path: AppRoutesPaths.Programs,
      layout: LayoutType.Fullscreen,
    },
    undergraduate: {
      name: 'undergraduate',
      element: <DegreeHome />,
      path: AppRoutesPaths.Undergraduate,
      layout: LayoutType.Fullscreen,
    },
    learning: {
      name: 'learning',
      element: <LearningPage />,
      path: AppRoutesPaths.Learning,
      layout: LayoutType.Fullscreen,
    },
    community: {
      name: 'community',
      element: <CommunityPage />,
      path: AppRoutesPaths.Community,
      layout: LayoutType.Fullscreen,
    },
    settings: {
      name: 'settings',
      element: <SettingsPage />,
      path: AppRoutesPaths.Settings,
      layout: LayoutType.Fullscreen,
    },
    transcript: {
      name: 'transcript',
      element: <TranscriptPage />,
      path: AppRoutesPaths.Transcript,
      layout: LayoutType.Fullscreen,
    },
    onboarding: {
      name: 'onboarding',
      element: <Onboarding />,
      path: AppRoutesPaths.Onboarding,
      layout: LayoutType.Blank,
    },
    profile: {
      name: 'profile',
      element: <Profile />,
      path: AppRoutesPaths.Profile,
      layout: LayoutType.Fullscreen,
    },
    subscribe: {
      name: 'subscribe',
      element: <Plans />,
      path: AppRoutesPaths.Subscribe,
      layout: LayoutType.Fullscreen,
    },
    subscribeCheckout: {
      name: 'subscribeCheckout',
      element: <SubscribeCheckout />,
      path: AppRoutesPaths.SubscribeCheckout,
      layout: LayoutType.Fullscreen,
    },
    // Private, like the rest: the onboarding gate in guardedRoute.tsx cannot
    // fire here, because anyone who reached checkout already passed it.
    subscribeComplete: {
      name: 'subscribeComplete',
      element: <SubscribeConfirmation />,
      path: AppRoutesPaths.SubscribeConfirmation,
      layout: LayoutType.Fullscreen,
    },
    paymentFAQs: {
      name: 'payment-faqs',
      element: <NotFound homeRoute={AppRoutesPaths.Home} />,
      path: AppRoutesPaths.PaymentFAQS,
      layout: LayoutType.Fullscreen,
    },
  },

  public: {
    // Recipients open transcript secure links without an account.
    transcriptVerify: {
      name: 'transcriptVerify',
      element: <TranscriptVerify />,
      path: AppRoutesPaths.TranscriptVerify,
      layout: LayoutType.Blank,
    },
    certificateVerify: {
      name: 'certificateVerify',
      element: <CertificateVerify />,
      path: AppRoutesPaths.CertificateVerify,
      layout: LayoutType.Blank,
    },
    badgeVerify: {
      name: 'badgeVerify',
      element: <BadgeVerify />,
      path: AppRoutesPaths.BadgeVerify,
      layout: LayoutType.Blank,
    },

    login: {
      name: 'login',
      element: (
        <SignIn
          resetPasswordRoute={AppRoutesPaths.ResetPassword}
          homeRoute={AppRoutesPaths.Home}
          signUpRoute={AppRoutesPaths.SignUp}
          confirmSignUpRoute={AppRoutesPaths.ConfirmSignUp}
          updatePasswordRoute={AppRoutesPaths.CreatePassword}
          supportRoute={FIT_HELP_CENTER_URL}
          signInTitle="Sign In"
          signInSubtitle="Use your email to pick up where you left off."
          signInCTAText="Sign up here"
          identifierLabel="Email *"
          signInWithGoogleLogo={<GoogleLogo className="h-6 w-auto" />}
          signInWithLinkedInLogo={
            <LinkedInLogo className="h-6 w-auto fill-linkedin" />
          }
          signInWithEhubLogo={<Logo height={24} />}
          evenSocialButtons={true}
          socialButtonsClassName={signInSocialButtonsClassName}
          loginCover={loginCover}
          loginCoverOverlay={true}
          loginCoverOverlayClassName={fitCoverOverlayClassName}
          loginCoverLogo={fitCoverLogo}
          loginCoverLogoPosition="bottom"
          loginCoverMobileHeaderClassName="bg-heading"
          titleClassName="font-dmSans text-[42px] leading-[55px] font-semibold text-center text-primary"
          socialSeparatorClassName="font-normal"
          formSeparatorClassName="font-normal"
          formSeparatorTextClassName="!text-heading"
          signInButtonClassName="h-12 border border-[#1552D7]"
          identifierLabelClassName="font-dmSans text-foreground"
          identifierInputClassName={signInInputClassName}
          forgotPasswordLinkClassName="text-tertiary"
          rightCardClassName="pb-8 lg:pb-0"
          ctaClassName="text-base text-tertiary font-dmSans"
          ctaLinkClassName="text-base"
          supportClassName="text-base font-normal text-tertiary font-dmSans"
          supportBodyClassName="text-base text-tertiary font-dmSans"
          supportLinkClassName="text-base"
          onSignInSuccess={() =>
            setPendingAuthEvent(AuthEventType.Login, LoginMethod.Email)
          }
        />
      ),
      path: AppRoutesPaths.Login,
      layout: LayoutType.Card,
    },

    signUp: {
      name: 'signUp',
      element: (
        <SignUp
          loginRoute={AppRoutesPaths.Login}
          confirmSignUpRoute={AppRoutesPaths.ConfirmSignUp}
          supportRoute={FIT_HELP_CENTER_URL}
          signUpWithGoogleLogo={<GoogleLogo className="h-6 w-auto" />}
          signUpWithLinkedInLogo={
            <LinkedInLogo className="h-6 w-auto fill-linkedin" />
          }
          signUpWithEhubLogo={<Logo height={24} />}
          socialButtonsClassName={fitSocialButtonsClassName}
          signUpCover={signUpCover}
          overlay={true}
          overlayClassName={fitCoverOverlayClassName}
          coverLogo={fitCoverLogo}
          coverLogoPosition="bottom"
          coverMobileHeaderClassName="bg-heading"
          rightCardClassName="pb-8 lg:pb-0"
          signUpTitle="Create your account"
          signUpSubtitle="One account gives you access to Global Skills Academy and Frontier Institute of Technology Undergraduate Programs. After signing up, you'll add a few details and choose your path toward your career goals."
          signUpButtonLabel="Start Now"
          titleClassName="font-dmSans text-[42px] leading-[55px] font-semibold text-center text-primary"
          socialSeparatorText="sign up with"
          formSeparatorText="or sign up manually"
          privacyPolicyLink="https://frontiertech.edu/privacy-policy"
          termsOfServiceLink="https://www.frontiertech.edu/terms-of-use"
          brandName="Frontier"
          showRefereeMarketing={false}
          hideNameFields={true}
          showConfirmPassword={true}
          hideMarketingConsent={true}
          legalLinkClassName="text-primary no-underline"
          privacyPolicyPrefix="the"
          ctaClassName="text-base text-tertiary font-dmSans"
          ctaLinkClassName="text-base"
          ctaLinkText="Sign in here"
          supportClassName="text-base font-normal text-tertiary font-dmSans"
          supportBodyClassName="text-base text-tertiary font-dmSans"
          supportLinkClassName="text-base"
          onReferralPageView={() =>
            trackEvent(AnalyticsEvent.ReferralSignupPageView, utmParams())
          }
          onSignUpSubmit={() =>
            trackEvent(AnalyticsEvent.ReferralSignupSubmitted, utmParams())
          }
        />
      ),
      path: AppRoutesPaths.SignUp,
      layout: LayoutType.Card,
    },

    confirmSignUp: {
      name: 'confirmSignUp',
      element: (
        <ConfirmSignUp
          codeByLink
          loginRoute={AppRoutesPaths.Login}
          confirmSignUpCover={changePasswordCover}
          afterAutoSignIn={AppRoutesPaths.Home}
          showRefereeMarketing={false}
          coverLogo={fitCoverLogo}
          coverLogoPosition="bottom"
          overlay={true}
          overlayClassName={fitCoverOverlayClassName}
          onSignUpComplete={() =>
            setPendingAuthEvent(AuthEventType.Signup, LoginMethod.Email)
          }
        />
      ),
      path: AppRoutesPaths.ConfirmSignUp,
      layout: LayoutType.Card,
    },

    changePassword: {
      name: 'changePassword',
      element: (
        <CreatePassword
          resetPasswordRoute={AppRoutesPaths.ResetPassword}
          homeRoute={AppRoutesPaths.Home}
          loginCover={loginCover}
          coverLogo={fitCoverLogo}
          coverLogoPosition="bottom"
          overlay={true}
          overlayClassName={fitCoverOverlayClassName}
        />
      ),
      path: AppRoutesPaths.CreatePassword,
      layout: LayoutType.Card,
    },

    resetPassword: {
      name: 'resetPassword',
      element: (
        <ResetPassword
          codeByLink
          loginRoute={AppRoutesPaths.Login}
          forgotPasswordCover={forgotPasswordCover}
          coverLogo={fitCoverLogo}
          coverLogoPosition="bottom"
          overlay={true}
          overlayClassName={fitCoverOverlayClassName}
          coverMobileHeaderClassName="bg-heading"
        />
      ),
      path: AppRoutesPaths.ResetPassword,
      layout: LayoutType.Card,
    },

    oauthToken: {
      name: 'oauthToken',
      layout: LayoutType.Blank,
      element: (
        <OAuthToken
          loginRoute={AppRoutesPaths.Login}
          homeRoute={AppRoutesPaths.Home}
          welcomeText="Welcome to Global Skills Academy."
        />
      ),
      path: AppRoutesPaths.OauthToken,
    },

    notFound: {
      name: 'notFound',
      // The literal wildcard, which is the route PATTERN. Anything unmatched
      // lands here, including AppRoutesPaths.NotFound itself.
      path: '*',
      layout: LayoutType.Blank,
      element: <NotFound homeRoute={AppRoutesPaths.Home} />,
    },
  },
};
